import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 800).bind(this);
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number | Object | null} */
  itemToRemove = null;
  #invertMode = false;

  constructor() {
    super();
    this.pendingCartUpdates = {};
    this.debouncedSendUpdates = debounce(this.#sendCartUpdates.bind(this), 800);
    this._instanceId = Math.random().toString(36).substr(2, 9); // 生成随机实例ID
  }

  connectedCallback() {
  super.connectedCallback();

  document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#handleCartItemsClick);
  this.addEventListener('change', this.#handleCheckboxChange);
}

  disconnectedCallback() {
  super.disconnectedCallback();

  document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleCartItemsClick);
  this.removeEventListener('change', this.#handleCheckboxChange);
}

  #handleCartItemsClick = (event) => {
    // 确保只处理当前组件内部的点击
    if (!this.contains(event.target)) return;

    const btn = event.target.closest('#cart-select-all-btn');
    if (btn && this.contains(btn)) {
      // console.log(`[${this._instanceId}] Clicked: Edit Button (#cart-select-all-btn)`, this);
      event.stopPropagation(); // 阻止事件冒泡，防止外层嵌套组件重复触发
      this.#toggleSelectionMode();
      return;
    }

    const toggleSelectBtn = event.target.closest('#cart-toggle-select-btn');
    if (toggleSelectBtn && this.contains(toggleSelectBtn)) {
      // console.log(`[${this._instanceId}] Clicked: Select All/Inverse Button (#cart-toggle-select-btn)`, this);
      event.stopPropagation(); // 阻止事件冒泡
      this.#toggleSelectOrInvert();
      return;
    }

    const deleteBtn = event.target.closest('#cart-delete-selected-btn');
    if (deleteBtn && !deleteBtn.disabled && this.contains(deleteBtn)) {
      // console.log(`[${this._instanceId}] Clicked: Delete Button (#cart-delete-selected-btn)`, this);
      event.stopPropagation(); // 阻止事件冒泡
      this.#handleDeleteSelected();
      return;
    }

  // Handle row click in selection mode
  const wrapper = this.querySelector('.cart-items');
  if (wrapper && wrapper.classList.contains('cart-items--selection-mode')) {
    const row = event.target.closest('.cart-items__table-row');
    if (row) {
        // Ignore clicks on interactive elements
      if (event.target.closest('button, input, quantity-selector-component, label[for], .quantity-selector')) {
        return;
      }

        // Prevent navigation for links
      if (event.target.closest('a')) {
        event.preventDefault();
      }

      const checkbox = row.querySelector('.cart-item-checkbox');
      if (checkbox) {
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
};

  #handleDeleteSelected() {
    const wrapper = this.querySelector('.cart-items');
    if (!wrapper) return;

    const checkboxes = wrapper.querySelectorAll('.cart-item-checkbox:checked');
    if (checkboxes.length === 0) return;

    // Collect all keys to delete
    const updates = {};
    checkboxes.forEach(cb => {
      // Checkbox should have data-key or be inside a row with data-key
      const row = cb.closest('.cart-items__table-row');
      const key = row?.dataset.key;
      if (key) {
        updates[key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) return;

    this.itemToRemove = updates;
    const modal = this.querySelector('#cart-remove-confirmation');
    if (modal) {
        modal.classList.add('is-visible');
        const title = modal.querySelector('.cart-remove-confirmation__title');
        const message = modal.querySelector('.cart-remove-confirmation__message');
        
        const textTitle = modal.dataset.textRemoveSelectedTitle || 'Remove Selected Items?';
        const textMessage = modal.dataset.textRemoveSelectedMessage || 'Are you sure you want to remove the selected items from your cart?';

        if (title) title.textContent = textTitle;
        if (message) message.textContent = textMessage;
    } else if (!window.confirm('Are you sure you want to remove the selected items from your cart?')) {
        return;
    }
  }

  handleDeleteSelected() {
    this.#handleDeleteSelected();
  }

  #performDeleteSelected(updates) {
    // Add updates to pendingCartUpdates and send
    Object.assign(this.pendingCartUpdates, updates);

    // Animate all rows being deleted (same effect as single-item delete)
    const keysToDelete = Object.keys(updates);
    if (keysToDelete.length) {
      const rowsToAnimate = [];
      keysToDelete.forEach((key) => {
        const row = this.refs.cartItemRows.find((r) => r.dataset.key === key);
        if (row) {
          rowsToAnimate.push(row);
          // include nested lines tied to this row
          const nested = this.refs.cartItemRows.filter((r) => r.dataset.parentKey === key);
          rowsToAnimate.push(...nested);
        }
      });

      rowsToAnimate.forEach((row) => {
        const remove = () => row.remove();
        if (prefersReducedMotion()) return remove();
        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, remove);
      });
    }

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    // Send batched updates
    this.debouncedSendUpdates();
  }

  #handleCheckboxChange = (event) => {
    if (!event.target.matches('.cart-item-checkbox')) return;

    const wrapper = this.querySelector('.cart-items');
    const deleteBtn = this.querySelector('#cart-delete-selected-btn');
    
    if (!wrapper || !deleteBtn) return;

    const checkboxes = wrapper.querySelectorAll('.cart-item-checkbox');
    const isAnySelected = Array.from(checkboxes).some(cb => cb.checked);

    if (isAnySelected) {
      deleteBtn.disabled = false;
    } else {
      deleteBtn.disabled = true;
    }
  };

  #toggleSelectionMode() {
  const wrapper = this.querySelector('.cart-items');
  const btn = this.querySelector('#cart-select-all-btn');
  const deleteBtn = this.querySelector('#cart-delete-selected-btn');
  const toggleSelectBtn = this.querySelector('#cart-toggle-select-btn');
  
  if (wrapper) {
    const isSelectionMode = wrapper.classList.toggle('cart-items--selection-mode');
    
    // Update button text
    if (btn) {
      const textDone = btn.dataset.textDone || 'Done';
      const textEdit = btn.dataset.textEdit || 'Edit';
      btn.textContent = isSelectionMode ? textDone : textEdit;
    }

    // Show/Hide select/invert button
    if (toggleSelectBtn) {
      if (isSelectionMode) {
        toggleSelectBtn.classList.remove('hidden');
        const textSelectAll = toggleSelectBtn.dataset.textSelectAll || 'SELECT ALL';
        toggleSelectBtn.textContent = textSelectAll;
        this.#invertMode = false;
      } else {
        toggleSelectBtn.classList.add('hidden');
      }
    }

    // Handle Delete button visibility and state
    if (deleteBtn) {
      if (isSelectionMode) {
          deleteBtn.classList.remove('hidden');
      } else {
          deleteBtn.classList.add('hidden');
      }
      deleteBtn.disabled = true;
    }

    // Reset checkboxes when entering/exiting edit mode
    const checkboxes = wrapper.querySelectorAll('.cart-item-checkbox');
    checkboxes.forEach(cb => cb.checked = false);
  }
}

  toggleSelectionMode() {
    this.#toggleSelectionMode();
  }

  #toggleSelectOrInvert() {
    const wrapper = this.querySelector('.cart-items');
    const toggleSelectBtn = this.querySelector('#cart-toggle-select-btn');
    const deleteBtn = this.querySelector('#cart-delete-selected-btn');
    if (!wrapper || !toggleSelectBtn) return;

    const checkboxes = wrapper.querySelectorAll('.cart-item-checkbox');
    const textSelectAll = toggleSelectBtn.dataset.textSelectAll || 'SELECT ALL';
    const textInverse = toggleSelectBtn.dataset.textInverse || 'INVERSE';

    if (!this.#invertMode) {
      // Select all
      checkboxes.forEach(cb => cb.checked = true);
      toggleSelectBtn.textContent = textInverse;
      this.#invertMode = true;
    } else {
      // Invert selection
      checkboxes.forEach(cb => cb.checked = !cb.checked);
      toggleSelectBtn.textContent = textSelectAll;
      this.#invertMode = false;
    }

    // Update delete button state
    if (deleteBtn) {
      const isAnySelected = Array.from(checkboxes).some(cb => cb.checked);
      deleteBtn.disabled = !isAnySelected;
    }
  }

  toggleSelectOrInvert() {
    this.#toggleSelectOrInvert();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const key = lineItemRow?.dataset.key;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.itemToRemove = line;
    const modal = this.querySelector('#cart-remove-confirmation');
    if (modal) {
        modal.classList.add('is-visible');
        const title = modal.querySelector('.cart-remove-confirmation__title');
        const message = modal.querySelector('.cart-remove-confirmation__message');

        const textTitle = modal.dataset.textRemoveItemTitle || 'Remove Item?';
        const textMessage = modal.dataset.textRemoveItemMessage || 'Are you sure you want to remove this item from your cart?';

        if (title) title.textContent = textTitle;
        if (message) message.textContent = textMessage;
    }
  }

  /**
   * Handles clicks on the overlay to close the modal.
   * @param {Event} event - The click event.
   */
  onOverlayClick(event) {
    const modal = this.querySelector('#cart-remove-confirmation');
    if (modal && event.composedPath()[0] === modal) {
      this.cancelDelete();
    }
  }

  /**
   * Cancels the line item removal.
   */
  cancelDelete() {
    this.itemToRemove = null;
    const modal = this.querySelector('#cart-remove-confirmation');
    if (modal) modal.classList.remove('is-visible');
  }

  /**
   * Confirms the line item removal.
   */
  confirmDelete() {
    if (this.itemToRemove) {
      if (typeof this.itemToRemove === 'object') {
        this.#performDeleteSelected(this.itemToRemove);
      } else {
        this.performRemove(this.itemToRemove);
      }
      this.cancelDelete();
    }
  }

  /**
   * Performs the line item removal logic.
   * @param {number} line - The line item index.
   */
  performRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    const key = cartItemRowToRemove?.dataset.key;

    this.updateQuantity({
      line,
      key,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const id = key || line;
    this.pendingCartUpdates[id] = quantity;

    this.#disableCartItems();
    cartTotal?.shimmer();

    this.debouncedSendUpdates();
  }

  /**
   * Sends the pending cart updates.
   */
  async #sendCartUpdates() {
    const updates = { ...this.pendingCartUpdates };
    this.pendingCartUpdates = {}; // Clear pending updates immediately

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Use /cart/update.js with updates map to support batching
    // Shopify accepts { updates: { "id": qty, "id2": qty } }
    // Note: id can be line item key or variant ID. We use keys if available.
    const body = JSON.stringify({
      updates: updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const options = fetchConfig('json', { body });

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}`, options);
      const responseText = await response.text();
      
      if (!responseText) return;
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      // Note: /cart/update.js returns the cart object, errors might be top level or specific
      // but usually it just returns the new state.
      if (parsedResponseText.errors) {
        // Handle errors - complicated with batching, defaulting to generic error for first item
        // or falling back to simple error display if possible
        const firstKey = Object.keys(updates)[0];
        // Try to map key back to line number if possible, or just alert
        console.error(parsedResponseText.errors);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.#updateQuantitySelectors(parsedResponseText);

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      this.#updateCartQuantitySelectorButtonStates();

    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      // cartPerformance metrics might need adjustment for batching
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
